import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { useQuoteRequestsStore, useQuotesStore } from '../../lib/db';
import type { QuoteRequest } from '../../types';

interface QuoteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string | null;
}

interface FormData {
  title: string;
  description: string;
  validUntil: string;
  hotelProposals: Array<{
    name: string;
    category: number;
    nights: number;
    pricePerNight: number;
    total: number;
  }>;
  transportProposals: Array<{
    type: string;
    description: string;
    price: number;
  }>;
  activities: Array<{
    name: string;
    description: string;
    price: number;
  }>;
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
}

export default function QuoteFormModal({ isOpen, onClose, requestId }: QuoteFormModalProps) {
  const { getQuoteByRequestId, addQuote, updateQuote } = useQuotesStore();
  const { requests, updateRequest } = useQuoteRequestsStore();
  const request = requests.find(r => r.id === requestId) as QuoteRequest;
  const existingQuote = requestId ? getQuoteByRequestId(requestId) : null;

  const [formData, setFormData] = useState<FormData>(() => {
    if (existingQuote) {
      return {
        title: existingQuote.title,
        description: existingQuote.description,
        validUntil: existingQuote.validUntil.split('T')[0],
        hotelProposals: [],
        transportProposals: [],
        activities: [],
        items: existingQuote.items,
      };
    }
    return {
      title: `Devis - ${request?.destination || ''}`,
      description: '',
      validUntil: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      hotelProposals: [],
      transportProposals: [],
      activities: [],
      items: [],
    };
  });

  if (!isOpen || !request) return null;

  const calculateTotal = () => {
    const hotelTotal = formData.hotelProposals.reduce((sum, hotel) => sum + hotel.total, 0);
    const transportTotal = formData.transportProposals.reduce((sum, transport) => sum + transport.price, 0);
    const activitiesTotal = formData.activities.reduce((sum, activity) => sum + activity.price, 0);
    const itemsTotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    return hotelTotal + transportTotal + activitiesTotal + itemsTotal;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const quoteData = {
      id: existingQuote?.id || `quote-${Date.now()}`,
      requestId: request.id,
      ...formData,
      price: calculateTotal(),
      status: 'draft' as const,
      agencyId: request.agencyId,
      createdAt: new Date().toISOString(),
    };

    if (existingQuote) {
      updateQuote(existingQuote.id, quoteData);
    } else {
      addQuote(quoteData);
      updateRequest(request.id, { status: 'processing' });
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">
              {existingQuote ? 'Modifier le devis' : 'Nouveau devis'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations du client */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-4">Informations du client</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Client</p>
                  <p className="font-medium">{request.firstName} {request.lastName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Contact</p>
                  <p className="font-medium">{request.email}</p>
                  <p className="text-sm">{request.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Destination</p>
                  <p className="font-medium">{request.destination}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Voyageurs</p>
                  <p className="font-medium">{request.travelers} personne{request.travelers > 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>

            {/* Informations du devis */}
            <div>
              <h3 className="text-lg font-medium mb-4">Informations du devis</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre du devis
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valable jusqu'au
                  </label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
            </div>

            {/* Propositions d'hébergement */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Hébergement</h3>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      hotelProposals: [
                        ...prev.hotelProposals,
                        {
                          name: '',
                          category: 3,
                          nights: 1,
                          pricePerNight: 0,
                          total: 0,
                        },
                      ],
                    }));
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {formData.hotelProposals.map((hotel, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={hotel.name}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            hotelProposals: prev.hotelProposals.map((h, i) =>
                              i === index ? { ...h, name: e.target.value } : h
                            ),
                          }));
                        }}
                        placeholder="Nom de l'hôtel"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="w-32">
                      <select
                        value={hotel.category}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            hotelProposals: prev.hotelProposals.map((h, i) =>
                              i === index ? { ...h, category: parseInt(e.target.value) } : h
                            ),
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {[1, 2, 3, 4, 5].map(stars => (
                          <option key={stars} value={stars}>
                            {stars} étoile{stars > 1 ? 's' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        value={hotel.nights}
                        onChange={(e) => {
                          const nights = parseInt(e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            hotelProposals: prev.hotelProposals.map((h, i) =>
                              i === index
                                ? { ...h, nights, total: nights * h.pricePerNight }
                                : h
                            ),
                          }));
                        }}
                        placeholder="Nuits"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                        min="1"
                      />
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        value={hotel.pricePerNight}
                        onChange={(e) => {
                          const pricePerNight = parseInt(e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            hotelProposals: prev.hotelProposals.map((h, i) =>
                              i === index
                                ? { ...h, pricePerNight, total: h.nights * pricePerNight }
                                : h
                            ),
                          }));
                        }}
                        placeholder="Prix/nuit"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                        min="0"
                      />
                    </div>
                    <div className="w-32 py-2 px-3 bg-gray-50 rounded-md">
                      {hotel.total.toLocaleString('fr-FR')} DA
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          hotelProposals: prev.hotelProposals.filter((_, i) => i !== index),
                        }));
                      }}
                      className="p-2 text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Transport */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Transport</h3>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      transportProposals: [
                        ...prev.transportProposals,
                        {
                          type: '',
                          description: '',
                          price: 0,
                        },
                      ],
                    }));
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {formData.transportProposals.map((transport, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="w-48">
                      <select
                        value={transport.type}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            transportProposals: prev.transportProposals.map((t, i) =>
                              i === index ? { ...t, type: e.target.value } : t
                            ),
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      >
                        <option value="">Type de transport</option>
                        <option value="flight">Vol</option>
                        <option value="train">Train</option>
                        <option value="bus">Bus</option>
                        <option value="transfer">Transfert</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={transport.description}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            transportProposals: prev.transportProposals.map((t, i) =>
                              i === index ? { ...t, description: e.target.value } : t
                            ),
                          }));
                        }}
                        placeholder="Description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        value={transport.price}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            transportProposals: prev.transportProposals.map((t, i) =>
                              i === index ? { ...t, price: parseInt(e.target.value) } : t
                            ),
                          }));
                        }}
                        placeholder="Prix"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                        min="0"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          transportProposals: prev.transportProposals.filter((_, i) => i !== index),
                        }));
                      }}
                      className="p-2 text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Activités */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Activités</h3>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      activities: [
                        ...prev.activities,
                        {
                          name: '',
                          description: '',
                          price: 0,
                        },
                      ],
                    }));
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {formData.activities.map((activity, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="w-48">
                      <input
                        type="text"
                        value={activity.name}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            activities: prev.activities.map((a, i) =>
                              i === index ? { ...a, name: e.target.value } : a
                            ),
                          }));
                        }}
                        placeholder="Nom de l'activité"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={activity.description}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            activities: prev.activities.map((a, i) =>
                              i === index ? { ...a, description: e.target.value } : a
                            ),
                          }));
                        }}
                        placeholder="Description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        value={activity.price}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            activities: prev.activities.map((a, i) =>
                              i === index ? { ...a, price: parseInt(e.target.value) } : a
                            ),
                          }));
                        }}
                        placeholder="Prix"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                        min="0"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          activities: prev.activities.filter((_, i) => i !== index),
                        }));
                      }}
                      className="p-2 text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Services additionnels */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Services additionnels</h3>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      items: [
                        ...prev.items,
                        {
                          description: '',
                          quantity: 1,
                          unitPrice: 0,
                          total: 0,
                        },
                      ],
                    }));
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            items: prev.items.map((i, idx) =>
                              idx === index ? { ...i, description: e.target.value } : i
                            ),
                          }));
                        }}
                        placeholder="Description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const quantity = parseInt(e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            items: prev.items.map((i, idx) =>
                              idx === index
                                ? { ...i, quantity, total: quantity * i.unitPrice }
                                : i
                            ),
                          }));
                        }}
                        placeholder="Qté"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                        min="1"
                      />
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => {
                          const unitPrice = parseInt(e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            items: prev.items.map((i, idx) =>
                              idx === index
                                ? { ...i, unitPrice, total: i.quantity * unitPrice }
                                : i
                            ),
                          }));
                        }}
                        placeholder="Prix unitaire"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                        min="0"
                      />
                    </div>
                    <div className="w-32 py-2 px-3 bg-gray-50 rounded-md">
                      {item.total.toLocaleString('fr-FR')} DA
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          items: prev.items.filter((_, i) => i !== index),
                        }));
                      }}
                      className="p-2 text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Résumé des coûts et boutons d'action */}
            <div className="border-t pt-6 mt-6">
              <div className="flex justify-between items-start">
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {existingQuote ? 'Mettre à jour' : 'Créer le devis'}
                  </button>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-medium mb-2">Résumé des coûts</h4>
                  <div className="space-y-2">
                    {formData.hotelProposals.length > 0 && (
                      <div className="flex justify-between">
                        <span>Hébergement:</span>
                        <span>
                          {formData.hotelProposals
                            .reduce((sum, hotel) => sum + hotel.total, 0)
                            .toLocaleString('fr-FR')} DA
                        </span>
                      </div>
                    )}
                    {formData.transportProposals.length > 0 && (
                      <div className="flex justify-between">
                        <span>Transport:</span>
                        <span>
                          {formData.transportProposals
                            .reduce((sum, transport) => sum + transport.price, 0)
                            .toLocaleString('fr-FR')} DA
                        </span>
                      </div>
                    )}
                    {formData.activities.length > 0 && (
                      <div className="flex justify-between">
                        <span>Activités:</span>
                        <span>
                          {formData.activities
                            .reduce((sum, activity) => sum + activity.price, 0)
                            .toLocaleString('fr-FR')} DA
                        </span>
                      </div>
                    )}
                    {formData.items.length > 0 && (
                      <div className="flex justify-between">
                        <span>Services additionnels:</span>
                        <span>
                          {formData.items
                            .reduce((sum, item) => sum + item.total, 0)
                            .toLocaleString('fr-FR')} DA
                        </span>
                      </div>
                    )}
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between font-bold">
                        <span>Total:</span>
                        <span>{calculateTotal().toLocaleString('fr-FR')} DA</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Prix par personne: {Math.round(calculateTotal() / request.travelers).toLocaleString('fr-FR')} DA
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}